import { Button } from "@d12labs/ui/button";
import { ChevronDown, Download, Search } from "lucide-react";
import type React from "react";
import { useState } from "react";
import type { CategoryKey, TranscriptAnnotation, TranscriptLine } from "../types";
import { CATEGORY_LABELS } from "../types";
import { Avatar } from "./avatar";

const PAGE_SIZE = 50;

interface TranscriptPaneProps {
	lines: TranscriptLine[];
	activeDim: CategoryKey;
	activeSpeakerId: string;
	speakerNames: Map<string, string>;
	durationSec: number | null;
	audioRef: React.RefObject<HTMLAudioElement | null>;
	onAnnotationClick: (dim: CategoryKey) => void;
	onShowAll: () => void;
}

function formatTimestamp(sec: number): string {
	const m = Math.floor(sec / 60);
	const s = Math.floor(sec % 60);
	return `${m}:${s.toString().padStart(2, "0")}`;
}

function sentimentChipClass(s: string): string {
	if (s === "positive")
		return "border-semantic-success/30 bg-semantic-success/10 text-semantic-success";
	if (s === "negative") return "border-danger/30 bg-danger/10 text-danger";
	return "border-hairline bg-surface-2 text-ink-muted";
}

function sentimentDot(s: string): string {
	if (s === "positive") return "var(--color-success)";
	if (s === "negative") return "var(--color-danger)";
	return "var(--color-ink-tertiary)";
}

function AnnotationChip({
	ann,
	activeDim,
	onClick,
}: {
	ann: TranscriptAnnotation;
	activeDim: CategoryKey;
	onClick: (dim: CategoryKey) => void;
}) {
	const isActive = ann.dimensionKey === activeDim;
	return (
		<button
			type="button"
			onClick={() => onClick(ann.dimensionKey)}
			className={`inline-flex cursor-pointer items-center gap-1.5 rounded-md border px-2 py-0.5 text-meta ${sentimentChipClass(ann.sentiment)} ${isActive ? "ring-1 ring-primary/40" : ""}`}
		>
			<span
				className="size-2 flex-none rounded-full"
				style={{ background: sentimentDot(ann.sentiment) }}
			/>
			<span className="font-medium">{CATEGORY_LABELS[ann.dimensionKey]}</span>
			<span className="opacity-80">·</span>
			<span>{ann.note}</span>
		</button>
	);
}

export function TranscriptPane({
	lines,
	activeDim,
	activeSpeakerId: _activeSpeakerId,
	speakerNames,
	durationSec,
	audioRef,
	onAnnotationClick,
	onShowAll,
}: TranscriptPaneProps) {
	const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

	const totalAnnotations = lines.reduce((n, l) => n + l.annotations.length, 0);
	const dimAnnotations = lines.reduce(
		(n, l) => n + l.annotations.filter((a) => a.dimensionKey === activeDim).length,
		0,
	);

	const visibleLines = lines.slice(0, visibleCount);
	const remaining = lines.length - visibleCount;

	const handleLineClick = (line: TranscriptLine) => {
		if (audioRef.current && line.timestampSec != null) {
			audioRef.current.currentTime = line.timestampSec;
		}
	};

	return (
		<div className="overflow-y-auto">
			{/* Sticky sub-header */}
			<div className="sticky top-0 z-10 border-b border-hairline bg-canvas/95 px-6 pt-4 pb-3 backdrop-blur">
				<div className="flex items-center justify-between">
					<div>
						<div className="text-eyebrow uppercase text-ink-tertiary">TRANSCRIPT</div>
						<div className="mt-0.5 flex items-baseline gap-2">
							<h2 className="text-[17px] font-semibold leading-snug tracking-tight text-ink">
								{durationSec != null ? formatTimestamp(durationSec) : "—"} · {lines.length} lines ·{" "}
								{totalAnnotations} annotations
							</h2>
							{dimAnnotations > 0 ? (
								<>
									<span className="text-body-sm text-ink-subtle">
										filtered to <span className="text-ink-muted">{CATEGORY_LABELS[activeDim]}</span>
									</span>
									<button
										type="button"
										onClick={onShowAll}
										className="ml-2 text-meta text-ink-subtle hover:text-ink"
									>
										Show all →
									</button>
								</>
							) : null}
						</div>
					</div>
					<div className="flex items-center gap-1">
						<Button variant="ghost" size="sm" className="gap-1.5">
							<Search className="size-3.5" />
							Search
						</Button>
						<Button variant="ghost" size="sm" className="gap-1.5">
							<Download className="size-3.5" />
							Export
						</Button>
					</div>
				</div>
			</div>

			{/* Transcript body */}
			<div className="space-y-2 px-6 py-4">
				{visibleLines.map((line) => {
					const hasActiveDim = line.annotations.some((a) => a.dimensionKey === activeDim);
					const hasOtherDim = line.annotations.length > 0 && !hasActiveDim && dimAnnotations > 0;
					const speakerName = speakerNames.get(line.speakerId) ?? "Speaker";
					const firstName = speakerName.split(" ")[0] ?? speakerName;

					return (
						<button
							key={line.id}
							type="button"
							onClick={() => handleLineClick(line)}
							className={`grid w-full cursor-pointer grid-cols-[40px_1fr] gap-3 rounded-md px-3 py-2 text-left transition-opacity ${
								hasActiveDim
									? "bg-surface-1 ring-1 ring-primary/30"
									: line.annotations.length > 0
										? "bg-surface-1"
										: "hover:bg-surface-1"
							} ${hasOtherDim ? "opacity-40" : ""}`}
						>
							<div className="flex flex-col items-center pt-0.5">
								<Avatar name={speakerName} size="md" className="size-7 text-[10px]" />
								<span className="mt-1 font-mono text-[10px] text-ink-tertiary">
									{formatTimestamp(line.timestampSec)}
								</span>
							</div>
							<div className="min-w-0">
								<div className="text-body-sm leading-relaxed text-ink-muted">
									<span className="mr-1.5 font-medium text-ink">{firstName}</span>
									{line.text}
								</div>
								{line.annotations.length > 0 ? (
									<div className="mt-2 flex flex-wrap gap-1.5">
										{line.annotations.map((ann) => (
											<AnnotationChip
												key={`${ann.dimensionKey}-${ann.sentiment}`}
												ann={ann}
												activeDim={activeDim}
												onClick={onAnnotationClick}
											/>
										))}
									</div>
								) : null}
							</div>
						</button>
					);
				})}

				{remaining > 0 ? (
					<div className="pt-4 text-center">
						<Button
							variant="ghost"
							size="sm"
							onClick={() => setVisibleCount((v) => v + PAGE_SIZE)}
							className="gap-1.5"
						>
							<ChevronDown className="size-3.5" />
							Load {remaining} more lines
						</Button>
					</div>
				) : null}
			</div>
		</div>
	);
}
